/** The web app (SWA) bundle registry's vocabulary — docs/infra/swa-bundle.md § The registry. */

import { BUNDLE_ID_SOURCE_PATTERN } from "./bundle";

/**
 * A web app bundle's status: a provision or a delete in flight, a bundle that exists, a bundle that was deleted,
 * or a last run that failed.
 */
export type SwaBundleStatus = "provisioning" | "ready" | "deprovisioning" | "absent" | "failed";

/** Every member of {@link SwaBundleStatus}; each must have a writer in the provision workflow. */
export const SWA_BUNDLE_STATUSES: readonly SwaBundleStatus[] = [
	"provisioning",
	"ready",
	"deprovisioning",
	"absent",
	"failed",
];

/** Narrows an arbitrary value to {@link SwaBundleStatus}. */
export function isSwaBundleStatus(value: unknown): value is SwaBundleStatus {
	return typeof value === "string" && (SWA_BUNDLE_STATUSES as readonly string[]).includes(value);
}

/** Whether a run is still working on the bundle, so reconcile-on-read may settle it from the run. */
export function isSwaBundleInFlight(status: SwaBundleStatus): boolean {
	return status === "provisioning" || status === "deprovisioning";
}

/** One status write. The project number names the bundle as provisioned; every field is non-secret. */
export type SwaBundleStatusUpdate = {
	status: SwaBundleStatus;
	projectNumber: string;
	upn?: string;
	entityRef?: string;
	runId?: number;
	runAttempt?: number;
	runUrl?: string;
	iacVersion?: string;
	subscriptionId?: string;
	resourceGroup?: string;
	siteUrl?: string;
	postgresHost?: string;
	keyVaultUri?: string;
	backendName?: string;
};

/** Every field a status write may carry; the route refuses any other. */
export const SWA_BUNDLE_STATUS_UPDATE_FIELDS = [
	"status",
	"projectNumber",
	"upn",
	"entityRef",
	"runId",
	"runAttempt",
	"runUrl",
	"iacVersion",
	"subscriptionId",
	"resourceGroup",
	"siteUrl",
	"postgresHost",
	"keyVaultUri",
	"backendName",
] as const satisfies readonly (keyof SwaBundleStatusUpdate)[];

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const HOSTNAME =
	/^(?=.{1,253}$)[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/i;
// Azure's App Service name rule: 2-60 letters, digits and hyphens, no leading or trailing hyphen.
const APP_SERVICE_NAME = /^[a-z0-9][a-z0-9-]{0,58}[a-z0-9]$/i;
// Azure's resource group name rule.
const RESOURCE_GROUP = /^[-\w._()]{1,89}[-\w_()]$/;

function isHttpsUrl(value: unknown): boolean {
	if (typeof value !== "string") return false;
	try {
		return new URL(value).protocol === "https:";
	} catch {
		return false;
	}
}

const isPositiveInteger = (value: unknown) =>
	typeof value === "number" && Number.isSafeInteger(value) && value > 0;

/** Why a status write body is unacceptable, one message per problem; empty when it is fine. */
export function swaBundleStatusUpdateErrors(body: unknown): string[] {
	if (typeof body !== "object" || body === null || Array.isArray(body))
		return ["body must be an object"];
	const b = body as Record<string, unknown>;
	const errors: string[] = [];
	const known = new Set<string>(SWA_BUNDLE_STATUS_UPDATE_FIELDS);
	for (const key of Object.keys(b)) {
		if (!known.has(key)) errors.push(`unknown field ${JSON.stringify(key)}`);
	}
	if (!isSwaBundleStatus(b.status)) {
		errors.push(`status must be one of ${SWA_BUNDLE_STATUSES.join(", ")}`);
	}
	if (typeof b.projectNumber !== "string" || !BUNDLE_ID_SOURCE_PATTERN.test(b.projectNumber)) {
		errors.push("projectNumber must be 1-12 letters, digits or hyphens");
	}
	const optional: Array<[string, (v: unknown) => boolean, string]> = [
		["upn", (v) => typeof v === "string" && /^[^@\s]+@[^@\s]+$/.test(v), "an email address"],
		["entityRef", (v) => typeof v === "string" && v.startsWith("user:"), "a user entity ref"],
		["runId", isPositiveInteger, "a positive integer"],
		["runAttempt", isPositiveInteger, "a positive integer"],
		["runUrl", isHttpsUrl, "an https URL"],
		["iacVersion", (v) => typeof v === "string" && v !== "", "a non-empty string"],
		["subscriptionId", (v) => typeof v === "string" && GUID.test(v), "a subscription GUID"],
		[
			"resourceGroup",
			(v) => typeof v === "string" && RESOURCE_GROUP.test(v),
			"a resource group name",
		],
		["siteUrl", isHttpsUrl, "an https URL"],
		["postgresHost", (v) => typeof v === "string" && HOSTNAME.test(v), "a host name"],
		["keyVaultUri", isHttpsUrl, "an https URL"],
		["backendName", (v) => typeof v === "string" && APP_SERVICE_NAME.test(v), "a web app name"],
	];
	for (const [field, valid, what] of optional) {
		if (b[field] !== undefined && !valid(b[field]))
			errors.push(`${field}, when given, must be ${what}`);
	}
	return errors;
}

/** One web app bundle as the registry serves it. `bundleId` is canonical; `projectNumber` as provisioned. */
export type SwaBundle = {
	bundleType: "swa";
	bundleId: string;
	projectNumber: string;
	slug: string;
	status: SwaBundleStatus;
	upn?: string;
	entityRef?: string;
	subscriptionId?: string;
	resourceGroup?: string;
	siteUrl?: string;
	postgresHost?: string;
	keyVaultUri?: string;
	backendName?: string;
	runId?: number;
	runUrl?: string;
	iacVersion?: string;
	createdAt: string;
	updatedAt: string;
};

/** `GET` {@link SWA_BUNDLES_MINE_PATH} and {@link swaBundlesPath}: a colleague's bundles, newest first. */
export type SwaBundlesResponse = { bundles: SwaBundle[] };

/** What a status write answers: `recorded`, or `stale` when a newer run already owns the row. */
export type SwaBundleStatusResponse = { result: "recorded" | "stale" };

/** Service-only status write for one colleague's bundle (the body names which). */
export function swaBundleStatusPath(slug: string): string {
	return `/registry/swa/status/${encodeURIComponent(slug)}`;
}

/** What a delete reservation answers: the bundle as it was, and what to put back if the dispatch is rejected. */
export type SwaDeleteReservation = {
	bundle: SwaBundle;
	previousStatus: SwaBundleStatus;
	previousRun?: { runId?: number; runAttempt?: number; runUrl?: string };
};

/** Who asks for a delete or gives a claim back: the registry refuses a row that names someone else. */
export type SwaDeleteOwner = { entityRef: string; upn: string };

/** The body of a delete reservation: the project, and the colleague whose bundle it must be. */
export type SwaDeleteReservationRequest = { projectNumber: string; owner: SwaDeleteOwner };

/** The body that gives a reservation back: the project, the owner and what {@link SwaDeleteReservation} held. */
export type SwaDeleteRelease = {
	projectNumber: string;
	owner: SwaDeleteOwner;
	previousStatus: SwaBundleStatus;
} & NonNullable<SwaDeleteReservation["previousRun"]>;

/** Whether `value` is a well-formed {@link SwaDeleteOwner}. */
export function isSwaDeleteOwner(value: unknown): value is SwaDeleteOwner {
	const o = value as Partial<SwaDeleteOwner> | null | undefined;
	return (
		typeof o?.entityRef === "string" &&
		o.entityRef.startsWith("user:") &&
		typeof o.upn === "string" &&
		o.upn !== ""
	);
}

/** Service-only: `swa:delete` gives its claim back after GitHub definitively rejected the dispatch. */
export function swaDeleteReleasePath(slug: string): string {
	return `/registry/swa/release-delete/${encodeURIComponent(slug)}`;
}

/** Service-only: `swa:delete` claims one bundle for a delete (body {@link SwaDeleteReservationRequest}) before it dispatches. */
export function swaDeleteReservationPath(slug: string): string {
	return `/registry/swa/reserve-delete/${encodeURIComponent(slug)}`;
}

/** Service-only read of one colleague's bundles. */
export function swaBundlesPath(slug: string): string {
	return `/registry/swa/bundles/${encodeURIComponent(slug)}`;
}

/** The signed-in colleague's own bundles; the slug comes from the session, never the request. */
export const SWA_BUNDLES_MINE_PATH = "/registry/swa/mine";

/** The owner downloads the bundle's `.env` here; the slug comes from the session, never the request. */
export function swaAppEnvPath(projectNumber: string): string {
	return `${SWA_BUNDLES_MINE_PATH}/${encodeURIComponent(projectNumber)}/app-env`;
}

/** The owner reads the site's visitor password here (`{ visitorPassword }`); the slug comes from the session. */
export function swaVisitorPasswordPath(projectNumber: string): string {
	return `${SWA_BUNDLES_MINE_PATH}/${encodeURIComponent(projectNumber)}/visitor-password`;
}

/** The colleague's own page: the request template and every notification link there. */
export const SWA_OWNER_PAGE_PATH = "/my-web-apps";

/** The request template's catalog name; `/my-web-apps/new` renders its form. */
export const SWA_PROVISION_TEMPLATE_NAME = "provision-swa";

/** The delete template's catalog name; `/my-web-apps/delete` renders its form. */
export const SWA_DELETE_TEMPLATE_NAME = "delete-swa";

/** A project number as the request form takes it: a bundle id without hyphens (bundle contract § Inputs). */
export const SWA_PROJECT_NUMBER_PATTERN = /^[A-Za-z0-9]{1,12}$/;

/** How many laptop ranges one request may allow through the Postgres firewall. */
export const SWA_ALLOWED_CIDRS_MAX = 5;

// No leading zeros, so one address has one spelling and the duplicate check holds.
const OCTET = "(0|[1-9]\\d{0,2})";
const IPV4_CIDR = new RegExp(`^${OCTET}\\.${OCTET}\\.${OCTET}\\.${OCTET}\\/(\\d{1,2})$`);

// Ranges no laptop reaches Azure from: private, loopback, link-local, carrier NAT, IETF protocol
// assignments, documentation, benchmarking, multicast and reserved (IANA special-purpose registry).
const NOT_PUBLIC: Array<[number, number, string]> = [
	[0x00000000, 8, "0.0.0.0/8"],
	[0x0a000000, 8, "10.0.0.0/8"],
	[0x64400000, 10, "100.64.0.0/10"],
	[0x7f000000, 8, "127.0.0.0/8"],
	[0xa9fe0000, 16, "169.254.0.0/16"],
	[0xac100000, 12, "172.16.0.0/12"],
	[0xc0a80000, 16, "192.168.0.0/16"],
	[0xc0000000, 24, "192.0.0.0/24"],
	[0xc0000200, 24, "192.0.2.0/24"],
	[0xc6120000, 15, "198.18.0.0/15"],
	[0xc6336400, 24, "198.51.100.0/24"],
	[0xcb007100, 24, "203.0.113.0/24"],
	[0xe0000000, 3, "224.0.0.0/3"],
];

// The two addresses in 192.0.0.0/24 that IANA marks globally reachable (192.0.0.9 and 192.0.0.10).
const GLOBAL_IN_PROTOCOL_BLOCK = new Set([0xc0000009, 0xc000000a]);

/**
 * Why a list of laptop CIDRs cannot open the Postgres firewall; empty when it can. The tofu root's
 * `allowed_cidrs` validations are the floor; this adds the count and the public-address rule.
 */
export function allowedCidrErrors(cidrs: unknown): string[] {
	if (!Array.isArray(cidrs) || !cidrs.every((c) => typeof c === "string")) {
		return ["allowed IP ranges must be a list of text"];
	}
	const errors: string[] = [];
	if (cidrs.length < 1 || cidrs.length > SWA_ALLOWED_CIDRS_MAX) {
		errors.push(`give between 1 and ${SWA_ALLOWED_CIDRS_MAX} IP ranges`);
	}
	if (new Set(cidrs).size !== cidrs.length) errors.push("an IP range is listed twice");
	for (const cidr of cidrs as string[]) {
		const m = IPV4_CIDR.exec(cidr);
		const octets = m ? m.slice(1, 5).map(Number) : [];
		if (!m || octets.some((o) => o > 255)) {
			errors.push(`"${cidr}" is not an IPv4 range such as 203.0.113.7/32`);
			continue;
		}
		if (Number(m[5]) < 24 || Number(m[5]) > 32) {
			errors.push(`"${cidr}" must be /24 or narrower: a wider range admits strangers`);
			continue;
		}
		const address = octets.reduce((acc, o) => acc * 256 + o, 0);
		if (m[5] === "32" && GLOBAL_IN_PROTOCOL_BLOCK.has(address)) continue;
		const blocked = NOT_PUBLIC.find(
			([net, bits]) => Math.floor(address / 2 ** (32 - bits)) === net / 2 ** (32 - bits),
		);
		if (blocked) {
			errors.push(
				`"${cidr}" is in ${blocked[2]}, not a public address: use the address the internet sees`,
			);
		}
	}
	return errors;
}
